/**
 * Renders the "daterange" kind: two endpoints in one wrapper, a toggle, and a calendar popup that
 * edits a draft and writes it as soon as both ends are known.
 *
 * What a range means — which pick starts it, which closes it, which cells fall between, what the
 * bounds refuse — is `createDaterangeFieldController`'s. This renderer owns DOM and events, and
 * paints the cells the controller hands it.
 */
import { applyOpenerPromise } from "../opener-promise.js";
import { observerFor } from "@modyra/core";
import { buildDateLocale, formatIsoDate, formatLocalizedDate, parseIsoDate, parseLocalizedDate, today } from "@modyra/core/datetime";
import { fieldAccessibleName, applySubmissionNames, syncSubmission, MDY_WIDGET_CONTRACTS, createDaterangeFieldController, defaultWidgetIdFactory, overlayAnchoringFor, projectFieldShellA11y, shownErrorsOf, dateEntryText, fieldCanBeInvalid, visibleErrorsOf, MDY_I18N_MESSAGES_DEFAULT, keyBindingFor, MDY_PART_NAMES, presentationClass, calendarViewOnToggle, overlayControlledId, daterangeFieldPartIds } from "@modyra/widgets";
import { applyPart, el, setErrors, setText, setIcon } from "../dom.js";
import { buildFieldShell, insertControl } from "../field-shell.js";
import { withControls } from "../field-controls.js";
import { dismissOnFocusOutside } from "../overlay.js";
import { dismissOnOutsidePointer, positionOverlay, releaseOverlayPlacement, reflectOverlayOpen, trackOverlay } from "../overlay.js";
import { bindViewZoom, buildCalendarGrid, buildPeriodViews, fillCalendar } from "./calendar.js";
/**
 * The class every part and box wears, asked of the contract once.
 *
 * Spelled here too, each of these is a second copy of a name the catalogue holds — and the copy is
 * where the two come to disagree without either moving.
 */
const CLASS = {
    box: presentationClass("daterange", "box"),
};
export function renderDaterangeField(container, f, handle, reactivity, options = {}, widgetId = f.name, 
/**
 * The words this control shows. The engine has no opinion about them, so they arrive from the
 * widget contract's tables rather than being written here — three renderers each spelling
 * "open the calendar" is three answers to one question.
 */
messages = MDY_I18N_MESSAGES_DEFAULT) {
    reactivity = observerFor(handle, reactivity);
    // How this popup attaches is the contract's, not this renderer's.
    const anchoring = overlayAnchoringFor("daterange");
    const definition = MDY_WIDGET_CONTRACTS.daterange;
    const bounds = { minIso: options.minDate ?? null, maxIso: options.maxDate ?? null };
    // Month and weekday names, and which day starts the week, all come from Intl through
    // `buildDateLocale` — the same bundle every renderer is given by DI.
    const dateLocale = buildDateLocale(options.locale ?? (typeof navigator === "undefined" ? "en-US" : navigator.language), options.firstDayOfWeek);
    const controller = createDaterangeFieldController({
        supportingText: () => f.supportingText ?? null,
        widgetId,
        handle: handle,
        minDate: bounds.minIso,
        maxDate: bounds.maxIso,
        firstDayOfWeek: dateLocale.firstDayOfWeek,
        // The text goes to the controller as text, and the locale is what this renderer knows: a
        // renderer that parsed here and dispatched only on success made an unreadable entry vanish,
        // because nothing was dispatched and the repaint below rewrote the box from a value that had
        // not changed.
        parseEntry: (text) => {
            const parsed = parseLocalizedDate(text, dateLocale.locale);
            return parsed ? formatIsoDate(parsed) : null;
        },
    }, reactivity);
    const shell = buildFieldShell(f.label, "daterange", {}, f.ariaLabel, f.name, f.supportingText);
    // The two boxes together are what the caption names: a person entering hears it once, then each
    // box's own role. Pointing the boxes at the caption instead gave a part the name of the whole, and
    // both ends announced the same words. ADR 0175.
    shell.wrapper.setAttribute("role", "group");
    // The caption's words rather than a reference to it: a reference is one more thing that can point
    // at nothing — a label a document did not ask for is not drawn — and a reader hears the same
    // sentence either way.
    if (typeof f.label === "string" && f.label.trim() !== "") {
        shell.wrapper.setAttribute("aria-label", f.label);
    }
    const wrapper = el("div", CLASS.box);
    const startInput = el("input", definition.parts.startControl.classes.join(" "));
    startInput.type = "text";
    startInput.autocomplete = "off";
    // The caption names this one, through its `for` — a range's caption belongs to where the range
    // starts, and the contract says so with that relation. A second name here would be two answers to
    // one question, and this one was built from an English word a translated page still said.
    startInput.placeholder = f.placeholder ?? messages.daterangeStartLabel;
    const separator = el("span", definition.parts.separator.classes.join(" "));
    separator.setAttribute("aria-hidden", "true");
    setText(separator, "–");
    const endInput = el("input", definition.parts.endControl.classes.join(" "));
    endInput.type = "text";
    endInput.autocomplete = "off";
    // Each end says its own role, from the table that holds those words rather than from a phrase a
    // renderer builds. The caption names the group the two stand in. ADR 0175, `MDY_PART_NAMES`.
    endInput.setAttribute("aria-label", messages[MDY_PART_NAMES["daterange.endControl"]]);
    startInput.setAttribute("aria-label", messages[MDY_PART_NAMES["daterange.startControl"]]);
    endInput.placeholder = messages.daterangeEndLabel;
    const toggle = el("button", definition.parts.toggle.classes.join(" "));
    setIcon(toggle, "CALENDAR");
    toggle.type = "button";
    toggle.setAttribute("aria-label", messages.daterangeChooseRange);
    // Asked of the contract rather than written here. The word is announced with the control, before
    // anything opens, and a person decides whether to open it from that word — so two renderers of one
    // widget saying different words is two different widgets as far as a screen reader is concerned.
    // Nineteen places wrote this attribute and one read the projection; this is the second.
    applyOpenerPromise(toggle, "daterange");
    const popup = el("div", `${MDY_WIDGET_CONTRACTS.daterange.parts.popup.classes.join(" ")} mdy-overlay`);
    // The toggle said it had a dialog and whether it was open, and never said which one. Naming it
    // is what ties opener to popup for assistive technology — the relation select has always had.
    popup.id = defaultWidgetIdFactory.part(widgetId, "popup");
    toggle.setAttribute("aria-controls", overlayControlledId("daterange", widgetId) ?? popup.id);
    const header = el("div", definition.parts.dialogHeader.classes.join(" "));
    const prevButton = el("button", definition.parts.headerPrevious.classes.join(" "));
    prevButton.type = "button";
    prevButton.setAttribute("aria-label", messages.datepickerPreviousMonth);
    setIcon(prevButton, "CHEVRON_LEFT");
    // The header's middle child opens the month view, which opens the year view — the same funnel as
    // the single-date picker, since a range far from the month on screen is as many clicks away.
    const viewToggle = el("button", definition.parts.viewToggle.classes.join(" "));
    viewToggle.type = "button";
    // The month as its own element inside the button. The grid is named by referring to it, and the
    // button carries a name of its own — "change view, currently …" — so a reference crossing into the
    // button would announce the way out instead of where you are. ADR 0212.
    const monthTitle = el("span", definition.parts.headerTitle.classes.join(" "));
    monthTitle.id = daterangeFieldPartIds(widgetId).headingId;
    viewToggle.append(monthTitle);
    const nextButton = el("button", definition.parts.headerNext.classes.join(" "));
    nextButton.type = "button";
    nextButton.setAttribute("aria-label", messages.datepickerNextMonth);
    setIcon(nextButton, "CHEVRON_RIGHT");
    header.append(prevButton, viewToggle, nextButton);
    const grid = buildCalendarGrid("daterange");
    // The element the promise names: the opener says it opens a grid, and the panel that holds this
    // one carries no role of its own (ADR 0210).
    grid.id = overlayControlledId("daterange", widgetId) ?? grid.id;
    // Named by the line that says the month, and by nothing else: a written label beside a reference
    // loses to it in the name computation every time. ADR 0212.
    grid.setAttribute("aria-labelledby", monthTitle.id);
    const { monthPicker, yearPicker, render: renderPeriodView } = buildPeriodViews("daterange", widgetId, dateLocale, () => ({ min: parseIsoDate(bounds.minIso), max: parseIsoDate(bounds.maxIso) }), dispatch);
    // Same frame as the single-date picker: the popup positions, the calendar lays out.
    const calendar = el("div", MDY_WIDGET_CONTRACTS.daterange.parts.calendar.classes.join(" "));
    // What the opener promises, carried by the thing it opens. The other two renderers wrote the role
    // here and this one wrote it nowhere, so a person was told a dialog had appeared and met an
    // unnamed `<div>`. Read from the catalogue rather than spelled again.
    applyPart(calendar, MDY_WIDGET_CONTRACTS.daterange.parts.calendar);
    // A dialog owes a name: announced as "dialog" and nothing else, a person is told a panel has
    // appeared and not what it is for. The field's own words where there are any, the kind's sentence
    // otherwise — the same order the other two renderers use.
    calendar.setAttribute("aria-label", fieldAccessibleName({ label: f.label, name: f.name }) || messages.daterangeChooseRange);
    calendar.append(header, grid, monthPicker, yearPicker);
    popup.append(calendar);
    // The start input is what the label names: a range has two controls and `for` can point at only
    // one. The other keeps the `aria-label` it already carried.
    startInput.id = `${widgetId}__start`;
    wrapper.append(startInput, separator, endInput, toggle, popup);
    insertControl(shell, wrapper);
    container.appendChild(shell.root);
    /** Commands the controller asks for, carried out where the DOM is. */
    function dispatch(intent) {
        for (const command of controller.dispatch(intent)) {
            if (command.type === "restore-focus")
                startInput.focus();
        }
    }
    // Committing from the calendar restores focus to the start input, so the endpoints are synced
    // unless the user is mid-edit.
    let typing = false;
    toggle.addEventListener("click", () => dispatch(controller.state().open ? { type: "cancel" } : { type: "open" }));
    /**
     * The text this field last put in each box.
     *
     * Leaving a box re-reads it so a half-written entry survives the way out of the field. What the
     * field wrote there itself is a *reading* of the value, not another spelling of it — parsing it
     * back asks the entry parser to accept the display format, and a named month is not something it
     * reads. Unchanged text is therefore not an entry.
     */
    const fieldWrote = new WeakMap();
    for (const [input, which] of [[startInput, "start"], [endInput, "end"]]) {
        input.addEventListener("input", () => { typing = true; });
        // One end at a time, as text. A range is written one box at a time, and committing only a whole
        // readable range threw away a half-written one on the way out of the field.
        input.addEventListener("change", () => {
            typing = false;
            if (input.value === fieldWrote.get(input))
                return;
            dispatch({ type: "type", end: which, text: input.value });
        });
        // Leaving commits what is in the box — a half-written range must survive the way out of the
        // field — and commits nothing else: a traversal is not an answer, so it does not make the field
        // speak. ADR 0167.
        input.addEventListener("blur", () => {
            typing = false;
            if (input.value === fieldWrote.get(input))
                return;
            dispatch({ type: "type", end: which, text: input.value });
        });
    }
    prevButton.addEventListener("click", () => dispatch({ type: "navigate-month", delta: -1 }));
    nextButton.addEventListener("click", () => dispatch({ type: "navigate-month", delta: 1 }));
    // The header goes to the top of the funnel and choosing narrows back down. Which view it opens
    // is `calendarViewOnToggle`'s answer, not this renderer's.
    viewToggle.addEventListener("click", () => dispatch({ type: "set-view-mode", mode: calendarViewOnToggle(controller.state().viewMode) }));
    // The other views, reached from the keyboard by the gesture the kind declares.
    bindViewZoom("daterange", [wrapper, popup], { days: grid, months: monthPicker, years: yearPicker }, () => controller.state(), (mode) => dispatch({ type: "set-view-mode", mode }));
    // The calendar's own keys, answered by the controller that owns the month — the same intent the
    // single-date sibling sends. Without this the grid took focus when the popup opened and then
    // answered nothing: a range was pickable with a pointer and with nothing else.
    grid.addEventListener("keydown", (event) => {
        if (["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "PageUp", "PageDown", "Home", "End", "Enter", " ", "Escape"].includes(event.key)) {
            event.preventDefault();
            dispatch({ type: "keydown", key: event.key, shiftKey: event.shiftKey, ctrlKey: event.ctrlKey, metaKey: event.metaKey });
        }
    });
    // Escape dismisses from wherever the user is. This overlay does not take focus when it opens, so
    // listening on the popup alone meant the handler could only ever fire if the user had already
    // reached inside it — the keyboard could open the range and not close it.
    // Escape cancels and Tab lets go: an overlay whose focus has moved on to the next field is a
    // panel floating over a control the user has already left. Both dismiss, and they differ in where
    // focus lands — Escape hands it back to the opener, Tab leaves it where the key was taking it.
    const onEscape = (event) => {
        // Only while something is open. Dismissing a closed popup is not free: the dismissal restores
        // focus to the start input, so every Tab inside this field pulled the keyboard back to where it
        // began — two boxes and no way out of them.
        if (!controller.state().open)
            return;
        // The binding, not the intent. Both dismissals are declared `cancel` and they differ in where
        // focus lands — `restoresFocus` is the field that says which — so asking only for the intent
        // answers `Tab` with `Escape`'s rule and hands the keyboard back to the control it was leaving.
        // The phase is asked rather than assumed: a shut control asked about the open phase answers with
        // the bindings of a panel that is not there. ADR 0168.
        const binding = keyBindingFor("daterange", event, controller.state().open);
        if (binding?.intent !== "cancel")
            return;
        dispatch({ type: "cancel", restoreFocus: binding.restoresFocus === true });
    };
    popup.addEventListener("keydown", onEscape);
    // Leaving the calendar ends the preview: the highlight belongs to where the pointer is, and a
    // pointer that has left is nowhere. The committed range comes back on its own, because it is what
    // `previewed` falls back to.
    grid.addEventListener("pointerleave", () => dispatch({ type: "preview", iso: null }));
    wrapper.addEventListener("keydown", onEscape);
    const undismiss = dismissOnOutsidePointer([wrapper], () => controller.state().open, () => dispatch({ type: "cancel" }));
    // The other half of how this kind says it is dismissed. A calendar left open behind a field
    // somebody has tabbed away from covers the next question and answers to a keyboard that has gone.
    const unfocusout = dismissOnFocusOutside("daterange", [wrapper, shell.root, popup], () => controller.state().open, () => dispatch({ type: "cancel" }), { pointer: undismiss, markVisited: () => handle.markAsTouched() });
    let cellEls = new Map();
    let renderedMonth = "";
    /** Whether the last render showed an open calendar's days, so the render that brings them back is told apart. */
    let showedTheDays = false;
    const untrack = trackOverlay(popup, shell.wrapper, () => controller.state().open, anchoring);
    /**
     * The range a second pick would produce, while the pointer is still deciding.
     *
     * The contract has carried this since the kind existed: `{ type: "preview", iso }` is an intent,
     * the controller publishes `previewed`, and the `gridcell` part declares `inRange`, `rangeStart`
     * and `rangeEnd`. This renderer already painted all three — from `state.previewed` — and never
     * told the controller where the pointer was, so the highlight could only ever show a range that
     * was already committed.
     *
     * Rebound with the cells: `fillCalendar` replaces them on a month change, and a listener on a
     * button that is no longer in the document tells nobody anything.
     */
    function trackPreview() {
        for (const [iso, button] of cellEls) {
            button.addEventListener("pointerenter", () => dispatch({ type: "preview", iso }));
            // A keyboard moves the range the same way a pointer does: the focused cell is where the
            // person is deciding, whichever device put them there.
            button.addEventListener("focus", () => dispatch({ type: "preview", iso }));
        }
    }
    const effectRef = reactivity.effect(() => {
        const state = controller.state();
        const anchor = { year: state.viewYear, month: state.viewMonth, day: 1 };
        // What the calendar paints is the previewed range, not the committed one: the highlight has to
        // follow the pointer before anything is decided.
        const value = state.open ? state.previewed : state.value;
        // The state-driven half of the contract. `definition.parts` is static — classes and shape — so
        // on its own it never said the range was invalid, required, disabled or described by its errors.
        const a11y = projectFieldShellA11y({ disabled: handle.disabled(), required: handle.required() }, shownErrorsOf(handle), {
            supportingText: f.supportingText ?? null,
            widgetId: widgetId,
            controlId: startInput.id,
            // What is *shown*, not what is wrong. Without it the projection falls back to "there is an
            // error at all", and both ends of a required range announce themselves as failing before
            // anybody has been at the field, while the list beside them renders nothing.
            errorsVisible: visibleErrorsOf(handle, "daterange").length > 0,
            // The container is pointed at while it is on the page, not only while it holds a message.
            errorsReserved: visibleErrorsOf(handle, "daterange").length > 0 || fieldCanBeInvalid({
                required: handle.required?.() ?? false,
                constraints: handle.constraints?.() ?? null,
                disabled: handle.disabled?.() ?? false,
            }),
        });
        applyPart(shell.root, definition.parts.root);
        applyPart(shell.label, a11y.label);
        applyPart(shell.description, a11y.description);
        applyPart(shell.errorList, a11y.error);
        // Merged, not applied twice: a second `applyPart` on the same element recomputes its classes
        // from the base it captured on the first call, which would silently drop the part's own.
        for (const [input, iso, part, outstanding] of [
            [startInput, value.start, definition.parts.startControl, state.entryText.start],
            [endInput, value.end, definition.parts.endControl, state.entryText.end],
        ]) {
            // What the field could not read stays where the person left it, so it can be corrected rather
            // than silently emptied.
            if (!typing) {
                const shown = dateEntryText(outstanding, formatLocalizedDate(iso, dateLocale.locale));
                input.value = shown;
                fieldWrote.set(input, shown);
            }
            // Both endpoints carry the state: a range half of which announces itself invalid is worse
            // than one that says nothing at all.
            applyPart(input, { ...part, attributes: { ...part.attributes, ...a11y.control.attributes } });
            input.disabled = handle.disabled();
            // The key each end submits under, decided by the contract rather than by this loop.
            // A read-only range refuses the typed date and the calendar's choice alike; the native
            // attribute stops the typing and the ARIA says why.
            input.readOnly = handle.readonly();
            // Present only while it is true, which is what the projection declares: `aria-readonly="false"`
            // is a claim about a state the control is not in, and the contract emits the attribute or
            // nothing rather than the word "false". ADR 0165's rule about verdicts, applied to a state.
            if (handle.readonly())
                input.setAttribute("aria-readonly", "true");
            else
                input.removeAttribute("aria-readonly");
        }
        // Neither box carries a key, and this runs after the parts are applied so nothing the projection
        // wrote survives it. A box shows its end the way a person writes a date, and a name on it would
        // send that spelling; the two dates the range holds travel in inputs of their own instead.
        applySubmissionNames(shell.root, "daterange", f.name);
        syncSubmission(shell.root, "daterange", f.name, handle.value(), handle.disabled());
        toggle.disabled = handle.disabled();
        toggle.setAttribute("aria-expanded", String(state.open));
        reflectOverlayOpen(popup, state.open, messages);
        // Anchored by the contract, like every other overlay: the placement, the size and the
        // coordinates are `anchorOverlay`'s, and this only measures and applies them.
        if (!state.open)
            releaseOverlayPlacement(popup);
        // The kind, because this one's empty is an object. Without it a value that *is* this field's
        // nothing reads as one that arrived from somewhere — a draft, a server, a scripted write — and
        // those are said at once rather than waiting for a turn. So a range nobody had been near stated
        // its refusal from the first paint, and refused and untouched looked identical: the state that
        // matters was the one that never changed.
        setErrors(shell.errorList, visibleErrorsOf(handle, "daterange").map((error) => error.message));
        shell.syncState({
            open: state.open,
            touched: handle.touched(), disabled: handle.disabled(), readonly: handle.readonly(),
            hasError: visibleErrorsOf(handle, "daterange").length > 0, filled: value.start !== null, required: handle.required(), constraints: handle.constraints?.() ?? null,
        });
        const monthKey = `${anchor.year}-${anchor.month}`;
        setText(monthTitle, `${dateLocale.monthNamesLong[anchor.month - 1]} ${anchor.year}`);
        viewToggle.setAttribute("aria-label", messages.datepickerChangeView(monthTitle.textContent ?? ""));
        // Paging belongs to the day view: in the other two the arrows would move a month nobody is
        // looking at.
        prevButton.hidden = state.viewMode !== "days";
        nextButton.hidden = state.viewMode !== "days";
        grid.hidden = state.viewMode !== "days";
        renderPeriodView(state.viewMode, state.viewYear, state.viewMonth);
        // A closed calendar holds no cells: the popup element stays, because it is built once and lives
        // as long as the field, and what is inside it is six weeks of buttons that a screen reader
        // counts and a Tab key walks through while nothing is open.
        if (!state.open) {
            if (renderedMonth !== "") {
                grid.replaceChildren();
                cellEls = new Map();
                renderedMonth = "";
            }
        }
        else if (renderedMonth !== monthKey) {
            // The second pick closes the range, writes it and shuts the popup — all the controller's,
            // because this kind's value contract says `live`.
            cellEls = fillCalendar(grid, "daterange", anchor.year, anchor.month, dateLocale, (cell) => dispatch({ type: "select-date", iso: cell.iso }), widgetId);
            renderedMonth = monthKey;
            trackPreview();
        }
        // Which cell is an endpoint and which falls between them is the controller's answer. Comparing
        // ISO strings here was a fourth opinion, and it could not see a preview at all.
        const byIso = new Map(state.cells.map((cell) => [cell.iso, cell]));
        for (const [iso, button] of cellEls) {
            const cell = byIso.get(iso);
            if (!cell)
                continue;
            button.classList.toggle("mdy-datepicker__cell--range-start", cell.rangeStart);
            button.classList.toggle("mdy-datepicker__cell--range-end", cell.rangeEnd);
            button.classList.toggle("mdy-datepicker__cell--in-range", cell.inRange);
            button.classList.toggle("mdy-datepicker__cell--selected", cell.rangeStart || cell.rangeEnd);
            button.setAttribute("aria-selected", String(cell.rangeStart || cell.rangeEnd));
            // The day a calendar is always asked about, said and painted. The single-date sibling gets it
            // from the same door; this grid marked it in neither channel.
            const isToday = iso === formatIsoDate(today());
            button.classList.toggle("mdy-datepicker__cell--today", isToday);
            if (isToday)
                button.setAttribute("aria-current", "date");
            else
                button.removeAttribute("aria-current");
            // Where the keyboard is standing, which the controller answers and this grid was not painting:
            // the arrows moved a cursor nothing showed and nothing followed, so the calendar answered a
            // pointer and no key at all. One tab stop, on the cell the cursor is on.
            button.classList.toggle("mdy-datepicker__cell--focused", cell.focused);
            button.tabIndex = cell.focused ? 0 : -1;
            button.disabled = cell.disabled;
        }
        /**
         * Anchored after the month is in it.
         *
         * The panel measured before its cells exist is a fraction of the one a person sees, so the
         * policy is asked whether a box that size fits below the field — it does, and the calendar is
         * then drawn at full height where there was never room for it. The measurement is held for the
         * whole opening, so nothing later re-asks. One pass, in the right order.
         */
        if (state.open)
            positionOverlay(popup, shell.wrapper, anchoring);
        // A calendar takes focus into its grid when it opens, which its own datepicker sibling already
        // did and this one did not: a grid the keyboard cannot reach is a grid only a mouse can use.
        // The start endpoint is where the range begins, so that is where the user is put; failing that,
        // the first day that can be picked.
        // The render that brings the days back from the months or years takes the keyboard from the
        // header as well: the days are where a person returns to, whichever way they came back.
        const returnedToTheDays = !showedTheDays && state.open && state.viewMode === "days";
        showedTheDays = state.open && state.viewMode === "days";
        if (state.open) {
            const cursor = state.cells.find((cell) => cell.focused);
            const onCursor = cursor ? cellEls.get(cursor.iso) : undefined;
            if (returnedToTheDays && onCursor && popup.contains(document.activeElement) && !grid.contains(document.activeElement)) {
                onCursor.focus();
            }
            else if (!popup.contains(document.activeElement)) {
                const target = onCursor
                    ?? (value.start ? cellEls.get(value.start) : undefined)
                    ?? [...cellEls.values()].find((cell) => !cell.disabled);
                target?.focus();
            }
            else if (onCursor && grid.contains(document.activeElement) && document.activeElement !== onCursor) {
                // The cursor moved under a keyboard that is still in the grid, so focus goes with it —
                // otherwise the next arrow is measured from the cell the person left behind.
                onCursor.focus();
            }
        }
    });
    return withControls(() => {
        untrack();
        undismiss();
        unfocusout();
        controller.destroy();
        effectRef.destroy();
        shell.root.remove();
    }, 
    // Bounds move when a sibling field is answered — a return date that cannot precede a
    // departure — and the controller is told rather than the field remounted, which would forget
    // which end the next pick closes.
    { setBounds: (min, max) => controller.setBounds(min, max) });
}
